/**
 * Background preloading for Bulk Operations + Campaign Manager.
 * Populates liveEntityCache so tab switches and drill-downs feel instant.
 */

import { amazonApi } from './api';
import { previousPeriodBounds, resolveDateRangeBounds } from '../utils/dateRangeUtils';
import { normalizeCampaignType } from '../utils/amazonCampaignType';
import { loadCampaignMetricsBundle } from '../utils/campaignReportBundle';
import { warmBulkOperationsLevelsSlowly } from '../utils/bulkOperationsWarm';

/** Commonly used dashboard date filters — preloaded in background after active range. */
const COMMON_DASHBOARD_PRESETS = [
  { dateRange: 'today', period: '1' },
  { dateRange: '7d', period: '7' },
  { dateRange: '30d', period: '30' },
];

/** Warm 60d / 90d in the background so switching presets does not block the UI. */
const EXTENDED_DASHBOARD_PRESETS = [
  { dateRange: '60d', period: '60' },
  { dateRange: '90d', period: '90' },
];

const inFlight = new Map();
let activeBackgroundJobs = 0;
const MAX_BACKGROUND_JOBS = Number(process.env.REACT_APP_MAX_BACKGROUND_PRELOAD_JOBS) || 2;

function runBackgroundJob(label, fn) {
  if (activeBackgroundJobs >= MAX_BACKGROUND_JOBS) {
    console.log(`⏭️ Skipping background preload (${label}): ${activeBackgroundJobs} jobs already running`);
    return Promise.resolve();
  }
  activeBackgroundJobs += 1;
  return fn().finally(() => {
    activeBackgroundJobs = Math.max(0, activeBackgroundJobs - 1);
  });
}

const CAMPAIGN_BATCH = 8;
const AD_GROUP_BATCH = 4;
/** Per-campaign preload is expensive — skip when account is large. */
const MAX_PRELOAD_CAMPAIGN_DRILLDOWN = Number(process.env.REACT_APP_BULK_PRELOAD_CAMPAIGN_CAP) || 40;

function parseEntityList(response) {
  const payload = response?.data;
  if (!payload) return [];
  if (Array.isArray(payload)) return payload;
  if (payload.success && Array.isArray(payload.data)) return payload.data;
  if (Array.isArray(payload.data)) return payload.data;
  if (Array.isArray(payload.keywords)) return payload.keywords;
  return [];
}

async function runInBatches(items, batchSize, fn) {
  for (let i = 0; i < items.length; i += batchSize) {
    const chunk = items.slice(i, i + batchSize);
    await Promise.allSettled(chunk.map(fn));
  }
}

function preloadKey(kind, profileId, extra = '') {
  return `${kind}:${profileId}:${extra}`;
}

function periodToDateRangeKey(period) {
  if (period === '1') return 'today';
  if (period === '7') return '7d';
  if (period === 'thisMonth') return 'thisMonth';
  if (period === 'custom') return 'custom';
  return `${period}d`;
}

function dashboardPeriodKey({ period, startDate, endDate }) {
  return `${period}_${startDate}_${endDate}`;
}

/**
 * After Dashboard loads, warm the active range (full) plus today / 7d / 30d in background.
 */
function optimizationPreloadEnabled() {
  if (process.env.REACT_APP_DISABLE_OPTIMIZATION_PRELOAD === '1') return false;
  return process.env.REACT_APP_ENABLE_SERVER_OPTIMIZATION_WARMUP === '1';
}

const DEFER_HEAVY_PRELOAD_MS = Number(process.env.REACT_APP_DEFER_HEAVY_PRELOAD_MS) || 12000;

/**
 * Server-side: materialize merged display_metrics + warm bulk snapshot caches (campaign/ad group/keyword/search term).
 */
export function scheduleOptimizationWarmup(
  profileId,
  { startDate, endDate },
  { forceRefresh = false } = {}
) {
  if (!profileId || !startDate || !endDate) return Promise.resolve();
  if (!optimizationPreloadEnabled()) return Promise.resolve();

  const key = preloadKey('optimization-warmup', profileId, `${startDate}_${endDate}`);
  if (inFlight.has(key)) return inFlight.get(key);

  const job = amazonApi
    .postOptimizationWarmup(profileId, startDate, endDate, { forceRefresh })
    .catch((error) => {
      console.warn('⚠️ Optimization warmup partial failure:', error.message);
    })
    .finally(() => inFlight.delete(key));

  inFlight.set(key, job);
  return job;
}

export function scheduleDashboardPreload(
  profileId,
  { startDate, endDate, period = '30' },
  { forceRefresh = false, includeBulk = false } = {}
) {
  if (!profileId || !startDate || !endDate) return Promise.resolve();
  if (process.env.REACT_APP_DISABLE_DASHBOARD_PRELOAD === '1') return Promise.resolve();

  const activeKey = dashboardPeriodKey({ period, startDate, endDate });
  const preloadPresets = process.env.REACT_APP_DASHBOARD_PRELOAD_PRESETS === '1';
  const bulk = includeBulk === true;
  const serverWarmup = optimizationPreloadEnabled();

  const run = () => {
    const job = scheduleDashboardPeriodPreload(
      profileId,
      { startDate, endDate, period },
      { forceRefresh, includeBulk: bulk }
    )
      .then(() => {
        if (serverWarmup) {
          return scheduleOptimizationWarmup(profileId, { startDate, endDate }, { forceRefresh });
        }
        return Promise.resolve();
      })
      .then(() => (preloadPresets
        ? scheduleCommonDashboardPresetsPreload(profileId, activeKey, { forceRefresh })
        : Promise.resolve()))
      .catch((error) => {
        console.warn('⚠️ Dashboard preload partial failure:', error.message);
      });
    return job;
  };

  return new Promise((resolve) => {
    window.setTimeout(() => {
      resolve(runBackgroundJob('dashboard-preload', run));
    }, DEFER_HEAVY_PRELOAD_MS);
  });
}

/**
 * Preload today, 7d, and 30d dashboard caches (skips the range already loaded).
 */
const EXTENDED_PRELOAD_DEFER_MS = Number(process.env.REACT_APP_EXTENDED_DASHBOARD_PRELOAD_DEFER_MS) || 15000;

/**
 * After the dashboard is usable, prefetch 60d and 90d summaries (no bulk, low priority).
 */
export function scheduleExtendedDashboardRangePreloads(profileId, { activePeriodKey = '' } = {}) {
  if (!profileId) return Promise.resolve();
  if (process.env.REACT_APP_DISABLE_DASHBOARD_PRELOAD === '1') return Promise.resolve();
  if (process.env.REACT_APP_DISABLE_EXTENDED_DASHBOARD_PRELOAD === '1') return Promise.resolve();

  return new Promise((resolve) => {
    window.setTimeout(() => {
      const jobs = EXTENDED_DASHBOARD_PRESETS
        .map((preset) => {
          const { startDate, endDate } = resolveDateRangeBounds(preset.dateRange);
          const key = dashboardPeriodKey({ period: preset.period, startDate, endDate });
          if (key === activePeriodKey) return null;
          return scheduleDashboardPeriodPreload(
            profileId,
            { startDate, endDate, period: preset.period },
            { forceRefresh: false, includeBulk: false }
          );
        })
        .filter(Boolean);
      resolve(runBackgroundJob('extended-dashboard-preload', () => Promise.allSettled(jobs)));
    }, EXTENDED_PRELOAD_DEFER_MS);
  });
}

export function scheduleCommonDashboardPresetsPreload(
  profileId,
  activePeriodKey = '',
  { forceRefresh = false } = {}
) {
  if (!profileId) return Promise.resolve();

  return new Promise((resolve) => {
    window.setTimeout(() => {
      const jobs = COMMON_DASHBOARD_PRESETS
        .map((preset) => {
          const { startDate, endDate } = resolveDateRangeBounds(preset.dateRange);
          const key = dashboardPeriodKey({ period: preset.period, startDate, endDate });
          if (key === activePeriodKey) return null;
          return scheduleDashboardPeriodPreload(
            profileId,
            { startDate, endDate, period: preset.period },
            { forceRefresh, includeBulk: false }
          );
        })
        .filter(Boolean);
      resolve(Promise.allSettled(jobs));
    }, 8000);
  });
}

function scheduleDashboardPeriodPreload(
  profileId,
  { startDate, endDate, period },
  { forceRefresh = false, includeBulk = false } = {}
) {
  const key = preloadKey('dashboard-period', profileId, dashboardPeriodKey({ period, startDate, endDate }));
  if (inFlight.has(key)) return inFlight.get(key);

  const job = preloadDashboardPeriodCaches(
    profileId,
    { startDate, endDate, period },
    { forceRefresh, includeBulk }
  )
    .catch((error) => {
      console.warn(`⚠️ Dashboard period preload failed (${period}):`, error.message);
    })
    .finally(() => inFlight.delete(key));

  inFlight.set(key, job);
  return job;
}

export async function preloadDashboardCaches(
  profileId,
  { startDate, endDate, period = '30' },
  { forceRefresh = false, includeBulk = false } = {}
) {
  return scheduleDashboardPeriodPreload(
    profileId,
    { startDate, endDate, period },
    { forceRefresh, includeBulk }
  );
}

async function preloadDashboardPeriodCaches(
  profileId,
  { startDate, endDate, period = '30' },
  { forceRefresh = false, includeBulk = false } = {}
) {
  const dateRangeKey = periodToDateRangeKey(period);
  const label = dateRangeKey === 'today' ? 'today' : `${startDate} → ${endDate}`;
  console.log(`🔥 Preloading dashboard caches (${label})...`);

  let campaigns = null;
  const tasks = [
    amazonApi.getDashboardSummary(
      profileId,
      { period, startDate, endDate },
      { forceRefresh }
    ),
    loadCampaignMetricsBundle(profileId, startDate, endDate, dateRangeKey),
  ];

  if (includeBulk) {
    tasks.push(
      amazonApi.getMyStoreASINsWithMetrics(profileId, startDate, endDate, { forceRefresh })
    );
  }

  if (includeBulk) {
    tasks.push(
      amazonApi.getCampaigns(profileId, { forceRefresh }).then((response) => {
        campaigns = parseEntityList(response);
      }),
      amazonApi.getBulkSnapshotBundle(
        profileId,
        startDate,
        endDate,
        ['campaigns', 'adGroups', 'keywords', 'searchTerms', 'products']
      ),
      amazonApi.getAdGroupsPerformance(profileId, null, startDate, endDate, { forceRefresh }),
      amazonApi.getKeywordsPerformance(profileId, null, null, startDate, endDate, { forceRefresh }),
    );
  }

  await Promise.allSettled(tasks);

  if (includeBulk && campaigns?.length) {
    await scheduleBulkOperationsPreload(profileId, { startDate, endDate }, campaigns);
  }

  console.log(`✅ Dashboard preload finished (${label})`);
}

/**
 * After Bulk Ops campaigns load, warm caches for all other tabs in background.
 */
export function scheduleBulkOperationsPreload(profileId, { startDate, endDate }, campaigns = null) {
  if (!profileId) return Promise.resolve();
  const key = preloadKey('bulk', profileId, `${startDate}_${endDate}`);
  if (inFlight.has(key)) return inFlight.get(key);

  const job = runBackgroundJob('bulk-preload', () => preloadBulkOperationsCaches(profileId, { startDate, endDate }, campaigns))
    .catch((error) => {
      console.warn('⚠️ Bulk preload partial failure:', error.message);
    })
    .finally(() => inFlight.delete(key));

  inFlight.set(key, job);
  return job;
}

export async function preloadBulkOperationsCaches(profileId, { startDate, endDate }, campaigns = null) {
  let camps = campaigns;
  if (!camps?.length) {
    try {
      camps = parseEntityList(await amazonApi.getCampaigns(profileId));
    } catch {
      return;
    }
  }

  if (!camps.length) return;

  console.log(`🔥 Preloading bulk tabs for ${camps.length} campaigns (background)...`);

  await Promise.allSettled([
    amazonApi.getBulkSnapshotBundle(
      profileId,
      startDate,
      endDate,
      ['adGroups', 'keywords', 'searchTerms', 'products']
    ),
    amazonApi.getAdGroupsPerformance(profileId, null, startDate, endDate),
    amazonApi.getKeywordsPerformance(profileId, null, null, startDate, endDate),
    amazonApi.getProductAdsPerformance(profileId, null, null, startDate, endDate, null, { groupBy: 'adgroup' }),
  ]);

  if (camps.length > MAX_PRELOAD_CAMPAIGN_DRILLDOWN) {
    console.log(`⏭️ Skipping per-campaign bulk preload (${camps.length} campaigns)`);
    return;
  }

  await runInBatches(camps, CAMPAIGN_BATCH, async (campaign) => {
    const campaignId = campaign.campaignId || campaign.id;
    const campaignType = campaign.campaignType || campaign.adProduct || campaign.type;
    await Promise.allSettled([
      amazonApi.getAdGroups(profileId, campaignId, campaignType),
      amazonApi.getKeywords(profileId, campaignId, null, campaignType),
    ]);
  });

  await warmBulkOperationsLevelsSlowly();
  console.log('✅ Bulk tab preload finished');
}

/**
 * When user opens a campaign, preload all ad groups + per-ad-group entities.
 */
export function scheduleCampaignPreload(profileId, campaignId, campaignType, { startDate, endDate }) {
  if (!profileId || !campaignId) return Promise.resolve();
  const key = preloadKey('campaign', profileId, `${campaignId}_${startDate}_${endDate}`);
  if (inFlight.has(key)) return inFlight.get(key);

  const job = runBackgroundJob(`campaign-${campaignId}`, () => preloadCampaignCaches(
    profileId,
    campaignId,
    campaignType,
    { startDate, endDate }
  ))
    .catch((error) => {
      console.warn(`⚠️ Campaign preload partial failure (${campaignId}):`, error.message);
    })
    .finally(() => inFlight.delete(key));

  inFlight.set(key, job);
  return job;
}

export async function preloadCampaignCaches(profileId, campaignId, campaignType, { startDate, endDate }) {
  let adGroups = [];
  try {
    adGroups = parseEntityList(await amazonApi.getAdGroups(profileId, campaignId, campaignType));
  } catch {
    return;
  }

  if (!adGroups.length) return;

  console.log(`🔥 Preloading campaign ${campaignId} (${adGroups.length} ad groups)...`);

  const isSd = normalizeCampaignType(campaignType) === 'sponsored_display';

  await runInBatches(adGroups, AD_GROUP_BATCH, async (adGroup) => {
    const adGroupId = adGroup.adGroupId || adGroup.id;
    await Promise.allSettled(
      buildAdGroupTabPreloadJobs(profileId, campaignId, adGroupId, campaignType, startDate, endDate)
    );
  });

  console.log(`✅ Campaign preload finished: ${campaignId}`);
}

/**
 * When user opens a single ad group, ensure its shell is cached (cheap if already warm).
 */
/** Warm caches for ad group tabs (SP, SB, and SD). */
function buildAdGroupTabPreloadJobs(profileId, campaignId, adGroupId, campaignType, startDate, endDate) {
  const isSd = normalizeCampaignType(campaignType) === 'sponsored_display';
  const jobs = [
    amazonApi.getTargets(profileId, campaignId, adGroupId, campaignType),
    amazonApi.getProductAds(profileId, campaignId, adGroupId, campaignType),
    amazonApi.getTargetingData(profileId, campaignId, adGroupId, startDate, endDate),
    amazonApi.getSearchTerms(profileId, campaignId, adGroupId, startDate, endDate),
  ];
  if (isSd) {
    jobs.push(
      amazonApi.getSdCreatives(profileId, adGroupId),
      amazonApi.getSdNegativeTargets(profileId, campaignId, adGroupId),
    );
  } else {
    jobs.push(
      amazonApi.getKeywords(profileId, campaignId, adGroupId, campaignType),
      amazonApi.getNegativeKeywords(profileId, campaignId, adGroupId, campaignType),
      amazonApi.getCampaignNegativeKeywords(profileId, campaignId),
      amazonApi.getKeywordsPerformance(profileId, campaignId, adGroupId, startDate, endDate),
    );
    if (normalizeCampaignType(campaignType) === 'sponsored_brands') {
      jobs.push(amazonApi.getSbThemes(profileId, campaignId, adGroupId));
    }
  }
  return jobs;
}

export function scheduleAdGroupPreload(profileId, campaignId, adGroupId, campaignType, { startDate, endDate }) {
  if (!profileId || !campaignId || !adGroupId) return Promise.resolve();
  const key = preloadKey('adgroup', profileId, `${campaignId}_${adGroupId}`);
  if (inFlight.has(key)) return inFlight.get(key);

  const job = Promise.allSettled(
    buildAdGroupTabPreloadJobs(profileId, campaignId, adGroupId, campaignType, startDate, endDate)
  )
    .finally(() => inFlight.delete(key));

  inFlight.set(key, job);
  return job;
}
